import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  ListItemButton,
  Typography,
} from '@mui/material';
import { ActionButton, BrandLoader } from '@core/components';
import type { PastCombination } from '../../../../api/pastCombination';
import { PresetSummary } from '../../../components/PresetSummary';
import { useAppContext } from '../../../context/AppContext';
import type { VestmentType } from '../../../enum/vestmentType';
import { usePastCombinationPages } from '../../../hooks/usePastCombinationPages';
import {
  getDesignPresetSummary,
  getDesignPresetSummaryContext,
  getPresetFeatures,
} from '../../../utils/designPreset';

function pastCombinationLabel(combination: PastCombination): string {
  const customer = combination.customerName.trim() || 'Unknown customer';
  let monthYear = '';
  if (combination.orderCreatedAt) {
    try {
      monthYear = format(parseISO(combination.orderCreatedAt), 'MMM yyyy');
    } catch {
      monthYear = '';
    }
  }
  return monthYear ? `${customer} · ${monthYear}` : customer;
}

interface PastOrdersDrawerProps {
  open: boolean;
  designId: number;
  designType: VestmentType | null | undefined;
  onApply: (combination: PastCombination) => void;
  onClose: () => void;
}

export function PastOrdersDrawer({
  open,
  designId,
  designType,
  onApply,
  onClose,
}: PastOrdersDrawerProps) {
  const {
    vestments,
    fabrics,
    colors,
    threads,
    trimmings,
    galoons,
    cords,
    tassels,
  } = useAppContext();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const {
    combinations,
    isPending,
    isError,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    sentinelRef,
  } = usePastCombinationPages(open ? designId : null);

  useEffect(() => {
    if (open) {
      setSelectedId(null);
    }
  }, [open, designId]);

  const features = getPresetFeatures(designType);
  const summaryCtx = useMemo(
    () =>
      getDesignPresetSummaryContext(designType, {
        vestments,
        fabrics,
        colors,
        threads,
        trimmings,
        galoons,
        cords,
        tassels,
      }),
    [
      designType,
      vestments,
      fabrics,
      colors,
      threads,
      trimmings,
      galoons,
      cords,
      tassels,
    ]
  );

  const selected = combinations.find((item) => item.id === selectedId);

  const handleApply = () => {
    if (selected) {
      onApply(selected);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', sm: 360 },
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: { xs: 0, sm: 16 },
          borderBottomLeftRadius: { xs: 0, sm: 16 },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Past orders
        </Typography>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {isPending ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <BrandLoader />
          </Box>
        ) : isError ? (
          <Typography variant="body2" color="text.secondary">
            Couldn&apos;t load past combinations.
          </Typography>
        ) : combinations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No past combinations for this design.
          </Typography>
        ) : (
          <>
            {combinations.map((combination) => {
              const isSelected = selectedId === combination.id;
              return (
                <ListItemButton
                  key={combination.id}
                  role="option"
                  aria-selected={isSelected}
                  selected={isSelected}
                  onClick={() => setSelectedId(combination.id)}
                  sx={{
                    display: 'block',
                    flexGrow: 0,
                    border: 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1.25,
                    alignItems: 'flex-start',
                  }}
                >
                  <PresetSummary
                    name={pastCombinationLabel(combination)}
                    segments={
                      features
                        ? getDesignPresetSummary(
                            combination,
                            features,
                            summaryCtx
                          )
                        : []
                    }
                    selected={isSelected}
                  />
                </ListItemButton>
              );
            })}
            <Box ref={sentinelRef} sx={{ height: '1px' }} />
            {isFetchingNextPage ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <BrandLoader size={48} />
              </Box>
            ) : null}
            {isFetchNextPageError && !isFetchingNextPage ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  py: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Couldn&apos;t load more.
                </Typography>
                <ActionButton variant="outline" onClick={() => fetchNextPage()}>
                  Retry
                </ActionButton>
              </Box>
            ) : null}
          </>
        )}
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
        <ActionButton variant="outline" fullWidth onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton
          variant="primary"
          fullWidth
          disabled={selected == null}
          onClick={handleApply}
        >
          Apply
        </ActionButton>
      </Box>
    </Drawer>
  );
}
